/**
 * Drawing one card's label.
 *
 * A NIIMBOT prints from a one bit image, so a label is a canvas: black where
 * there should be ink, white everywhere else, no greys. At 203 dpi that is 8
 * dots to the millimetre, so the usual 50x30mm roll is 400 by 240 dots and
 * every measurement here is in dots rather than pixels or points.
 *
 * The vendor chooses what goes on it. Everything is optional because tables
 * differ: some price by condition, some do not grade at all, some want the set
 * so a buyer can tell two Charizards apart, and a 40mm label has no room for
 * all of it at once. What cannot be optional is legibility across a table, so
 * the name is always the biggest thing and the price is always the second.
 */

import QRCode from 'qrcode';
import { labelFace, ensureLabelFont } from './labelFont';

/** 203 dpi, the resolution of every NIIMBOT in this range. */
export const DOTS_PER_MM = 8;

/**
 * White left around the QR, in millimetres, on the three edges it touches.
 *
 * Small on purpose. Every tenth of a millimetre here comes straight out of the
 * module size, and the module size is the whole ballgame for whether a phone
 * reads this across a table in bad light.
 */
export const QR_CLEAR_MM = 0.75;

/**
 * The quiet zone, in modules, drawn by the encoder inside the square.
 *
 * The specification says four and this was set to one, which is a thing
 * scanners hunt for and often miss. It costs modules, which is why it was
 * tempting, and it is not negotiable.
 */
export const QR_QUIET_MODULES = 0;

/**
 * The biggest whole-dot square this code fits in, at or under `box` dots.
 *
 * A QR only renders cleanly when one module is a whole number of dots. Ask for
 * a size that does not divide and the encoder either pads it or the canvas gets
 * stretched, and every module lands between pixels.
 */
export function qrDots(url, box) {
  const modules = QRCode.create(url, { errorCorrectionLevel: 'M' }).modules.size;
  const scale = Math.max(1, Math.floor(box / modules));
  return modules * scale;
}

/**
 * The code, drawn a module at a time.
 *
 * Not `QRCode.toCanvas`. Handing a library a target size and trusting it to
 * divide leaves the rounding to the library: it centres what it produced inside
 * what you asked for, and a leftover of an odd number of dots lands one side
 * heavier than the other. On a 96 dot label that is a visibly fatter white band
 * under the code than over it.
 *
 * Reading the matrix and filling whole-dot squares removes the question. The
 * square is exactly modules x scale dots, so the gap above and below is exactly
 * (height - that) / 2, and both halves are whole dots.
 */
export function drawQr(ctx, url, x, y, size) {
  const qr = QRCode.create(url, { errorCorrectionLevel: 'M' });
  const n = qr.modules.size;
  const scale = Math.max(1, Math.floor(size / n));
  ctx.fillStyle = '#000000';
  for (let row = 0; row < n; row += 1) {
    for (let col = 0; col < n; col += 1) {
      if (qr.modules.get(row, col)) {
        ctx.fillRect(x + col * scale, y + row * scale, scale, scale);
      }
    }
  }
  return n * scale;
}

/**
 * The one thing about a label that is still a choice.
 *
 * Everything else is settled: the name, the condition and the code are on every
 * sticker, in the same places, at the same sizes. A design a vendor can
 * rearrange is a design that looks different on every table, and the only
 * question anyone actually asked was whether the price goes on at all - some
 * price at the table rather than on the sticker.
 */
export const LABEL_FIELDS = [
  { key: 'price', label: 'Price', hint: 'Off if you price at the table', defaultOn: true },
];

export const DEFAULT_FIELDS = LABEL_FIELDS
  .filter((f) => f.defaultOn)
  .map((f) => f.key);

/**
 * Label sizes a B1 or B21 takes, smallest first.
 *
 * Smallest first because the first print onto a real card showed the obvious
 * thing: a 50x30 sticker covers a third of a Pokemon card. A card is 63 by 88mm
 * and it is the card people want to look at, so the label wants to be a strip
 * along the bottom rather than a panel across the middle. 30x20 is the one to
 * reach for; the bigger ones stay for anyone labelling sealed product or a slab.
 *
 * Every size here is one NIIMBOT actually sells on a B-series roll, checked
 * against their own listings. Offering a size that cannot be bought is worse
 * than offering fewer: it renders a perfect preview for paper nobody has.
 */
export const LABEL_SIZES = [
  { key: '40x12', label: '40 x 12 mm', widthMm: 40, heightMm: 12, hint: 'What vendors use' },
  // Some 40 x 12 rolls are die-cut in pairs, and the printer's gap sensor reads
  // a pair as one label: it prints the first and feeds the second out blank,
  // wasting half the roll. This is the same 12mm label, told to fill both halves
  // with two different cards in one pass.
  {
    key: '40x12x2',
    label: '40 x 12 mm, 2 up',
    widthMm: 40,
    heightMm: 24,
    stack: 2,
    cellHeightMm: 12,
    hint: 'Roll with pairs',
  },
  { key: '30x15', label: '30 x 15 mm', widthMm: 30, heightMm: 15 },
  { key: '30x20', label: '30 x 20 mm', widthMm: 30, heightMm: 20, hint: 'Good on a card' },
  { key: '40x20', label: '40 x 20 mm', widthMm: 40, heightMm: 20 },
  { key: '40x30', label: '40 x 30 mm', widthMm: 40, heightMm: 30 },
  { key: '50x30', label: '50 x 30 mm', widthMm: 50, heightMm: 30, hint: 'Sealed product' },
  { key: '50x40', label: '50 x 40 mm', widthMm: 50, heightMm: 40 },
];

/**
 * What a new vendor starts on.
 *
 * Named rather than "the first one in the list", so reordering the sizes cannot
 * quietly change what everybody's labels come out as.
 */
export const DEFAULT_SIZE_KEY = '40x12';

/**
 * A price tag, in whole dollars.
 *
 * Cents cost four characters on a strip that has about twenty to spend, and
 * nobody at a table haggles over sixty-five of them. $25.65 becomes $26.
 */
export const money = (n) => {
  // Null and empty string both become 0 through Number(), and 0 is finite, so
  // an unpriced card printed a sticker reading $0. A vendor putting that on a
  // card is worse than a label with no price line at all.
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return `$${Math.round(v)}`;
};

/**
 * Cut text to the width it has, at a fixed size.
 *
 * A card name has no length limit worth trusting - a trainer like "Professor's
 * Research (Professor Sycamore)" runs past forty characters - so there is
 * nothing to shrink towards, and a name that silently runs off the edge of a
 * sticker is worse than one cut short with an ellipsis.
 *
 * Never shrunk, because the difference shows on a shelf: shrinking gives every
 * label a different type size, which reads as a mistake rather than a design.
 */
function clipText(ctx, text, maxWidth, px, weight) {
  ctx.font = labelFace(px, weight);
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

/**
 * Render one label.
 *
 * @param {object} card      the collection row: name, set_name, set_number, grade, price
 * @param {object} options
 * @param {string[]} options.fields  which of LABEL_FIELDS to draw
 * @param {object} options.size      one of LABEL_SIZES
 * @param {string} [options.qrUrl]   what the QR points at, when 'qr' is on
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderLabel(card, { fields = DEFAULT_FIELDS, size = LABEL_SIZES[0], qrUrl = null } = {}) {
  // Before anything measures: canvas substitutes silently when a face has not
  // loaded, at measure time as well as draw time, so a label laid out early is
  // laid out to the wrong widths.
  await ensureLabelFont();
  const on = (k) => fields.includes(k);
  const width = Math.round(size.widthMm * DOTS_PER_MM);
  const height = Math.round(size.heightMm * DOTS_PER_MM);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // White ground. A thermal printer burns black onto the label, so anything not
  // drawn is simply unburnt paper.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';

  // A short label is a different shape of problem, not the same one smaller.
  // The strip vendors actually use is 12mm tall - 96 dots - and 3mm of clearance
  // top and bottom would spend half of it on nothing. Below about 16mm the label
  // gets tighter margins, one line for the name, and the set line only if the
  // price still fits underneath it.
  // Everything scales off the label's height, so a taller roll gets the same
  // design rather than a stretched version of it.
  const scale = height / 96;
  const pad = Math.round(1.25 * DOTS_PER_MM * (size.widthMm / 40));
  const padY = Math.round(1 * DOTS_PER_MM * scale);
  const NAME = Math.round(19 * scale);
  const LEAD = Math.round(4 * scale);
  const PRICE_MAX = Math.round(42 * scale);

  // The code is the full height of the label, hard right, at a whole number of
  // dots per module. Its size never changes with what is on the card.
  const wantQr = qrUrl != null;
  const clear = Math.round(QR_CLEAR_MM * DOTS_PER_MM);
  const qrSize = wantQr ? qrDots(qrUrl, height - clear * 2) : 0;
  const qrX = width - clear - qrSize;
  const qrY = Math.round((height - qrSize) / 2);
  const textWidth = (wantQr ? qrX - DOTS_PER_MM : width - pad) - pad;

  // The name has the whole top line.
  //
  // One size, always, and an ellipsis past it. Shrinking to fit sounds kinder
  // and is not: a long name came out small AND clipped, and a shelf of labels
  // had a different name size on every sticker. Names have no ceiling worth
  // designing around either - a trainer card like "Professor's Research
  // (Professor Sycamore)" runs past forty characters - so there is nothing to
  // shrink towards.
  if (card.name) {
    const nm = clipText(ctx, card.name, textWidth, NAME, '600');
    ctx.font = labelFace(NAME, '600');
    ctx.fillText(nm, pad, padY);
  }

  // Price and condition share the bottom line, on one baseline, the way a price
  // carries its own suffix. The condition rode with the name at one point,
  // which cost the name four characters and left a gap beside the price doing
  // nothing - two or three letters fit down here for free.
  const priceText = on('price') ? money(card.price) : null;
  const grade = card.grade ? String(card.grade).toUpperCase() : null;

  if (priceText || grade) {
    const room = (height - padY) - (padY + NAME + LEAD);
    const ceiling = Math.min(PRICE_MAX, room);

    // Sized together, so a five figure price shrinks rather than pushing the
    // condition off the edge.
    const measure = (px) => {
      const condPx = Math.round(px * 0.45);
      ctx.font = labelFace(condPx, '600');
      const condW = grade ? Math.round(ctx.measureText(grade).width) : 0;
      ctx.font = labelFace(px, '700');
      const priceW = priceText ? Math.round(ctx.measureText(priceText).width) : 0;
      const gap = grade && priceText ? Math.round(px * 0.28) : 0;
      return { condPx, condW, priceW, gap, total: priceW + gap + condW };
    };

    let px = ceiling;
    while (px > Math.round(12 * scale) && measure(px).total > textWidth) px -= 1;
    const m = measure(px);

    const baseline = height - padY;
    ctx.textBaseline = 'alphabetic';
    let x = pad;
    if (priceText) {
      ctx.font = labelFace(px, '700');
      ctx.fillText(priceText, x, baseline);
      x += m.priceW + m.gap;
    }
    if (grade) {
      ctx.font = labelFace(m.condPx, '600');
      ctx.fillText(grade, x, baseline);
    }
    ctx.textBaseline = 'top';
  }

  if (wantQr) {
    // The label's own white is the quiet zone; no margin is drawn inside the
    // square, because modules spent on a margin are modules not spent on size.
    // M error correction stays: thermal labels smudge.
    drawQr(ctx, qrUrl, qrX, qrY, qrSize);
  }

  // Thermal heads are one bit: a grey pixel is either burnt or not, and letting
  // the printer decide gives muddy edges on small type. Everything is pushed to
  // pure black or pure white here instead.
  const img = ctx.getImageData(0, 0, width, height);
  const px = img.data;
  for (let i = 0; i < px.length; i += 4) {
    const lum = px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114;
    const v = lum < 160 ? 0 : 255;
    px[i] = v; px[i + 1] = v; px[i + 2] = v; px[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);

  return canvas;
}

/**
 * How far down the pair the printing starts, per cell, in dots.
 *
 * A paired roll is one label to the printer: the gap sensor finds the leading
 * edge of the pair and burning begins there, which is a fraction above where
 * the first sticker's face actually starts. On a single 12mm strip the sensor
 * finds every label, so the drift never accumulates and the margins come out
 * even. On a pair the top sticker wears all of it and the top of the design
 * touches the edge.
 *
 * So the image is nudged down the feed, most at the top and less further in,
 * which is the shape the drift has. The cost is at the very bottom of the
 * pair, which is where the slack is - the bottom and the two sides print
 * clean today.
 *
 * Whole dots, because a fractional offset is a resampled edge on a one-bit
 * head. Raise TOP if the top still crowds; the second cell follows at half.
 */
const FEED_NUDGE_TOP = 4;   // 0.5mm
const FEED_NUDGE_STEP = 2;  // each cell further down needs less

/** How far cell `i` of a pair moves down the feed, in whole dots. */
export function feedNudge(i) {
  return Math.max(0, FEED_NUDGE_TOP - i * FEED_NUDGE_STEP);
}

/**
 * One print that fills a paired label with two different cards.
 *
 * Each half is drawn by renderLabel at the cell size, so the layout, the
 * compact rules and the QR placement are the ones already proven on a single
 * 12mm strip - nothing here re-implements them.
 *
 * A trailing odd card is drawn in the top half and the bottom is left blank.
 * There is no way to feed half a label, so the alternative is not printing it.
 *
 * @param {Array<object>} cards  one or two cards, in feed order
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderLabelStack(cards, { fields = DEFAULT_FIELDS, size, qrUrlFor = null } = {}) {
  const cellHeightMm = size.cellHeightMm || size.heightMm / (size.stack || 1);
  const cell = { ...size, heightMm: cellHeightMm, stack: undefined };

  const width = Math.round(size.widthMm * DOTS_PER_MM);
  const height = Math.round(size.heightMm * DOTS_PER_MM);
  const cellHeight = Math.round(cellHeightMm * DOTS_PER_MM);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  for (let i = 0; i < (size.stack || 1); i += 1) {
    const card = cards[i];
    if (!card) break;
    // eslint-disable-next-line no-await-in-loop
    const one = await renderLabel(card, {
      fields,
      size: cell,
      qrUrl: qrUrlFor ? qrUrlFor(card) : null,
    });
    ctx.drawImage(one, 0, i * cellHeight + feedNudge(i));
  }

  return canvas;
}

/** How many cards one print consumes for this size. */
export function cardsPerPrint(size) {
  return size?.stack || 1;
}
